/**
 * Pure helpers for the shared date range filter (card #213).
 * Dates are local calendar days (time 00:00); nothing here reads the clock.
 */

export interface DateRangeFilterOption {
  value: string;
  label: string;
}

export interface DateRangeFilterRange {
  start: Date;
  end: Date;
}

/** Local midnight of a date, so two days compare by calendar day only. */
export function dayOf(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  return !!a && !!b && dayOf(a).getTime() === dayOf(b).getTime();
}

/** Inclusive number of calendar days in a range. */
export function daysInRange(start: Date, end: Date): number {
  return Math.round((dayOf(end).getTime() - dayOf(start).getTime()) / 86_400_000) + 1;
}

/** Earlier date first, whatever order they were clicked in. */
export function ordered(a: Date, b: Date): DateRangeFilterRange {
  return dayOf(a) <= dayOf(b) ? { start: dayOf(a), end: dayOf(b) } : { start: dayOf(b), end: dayOf(a) };
}

/**
 * Range classes for one calendar day; the class names styled by `_range-day.scss`.
 * A type alias, not an interface, so it also fits [ngClass]'s `{ [klass: string]: any }`.
 */
export type RangeDayClasses = {
  'edge-start': boolean;
  'edge-end': boolean;
  'in-range': boolean;
  solo: boolean;
};

const isDate = (d: Date | null | undefined): d is Date => d instanceof Date && !isNaN(d.getTime());

/**
 * How a day sits in the range between `a` and `b` (card #358): the range filter and the
 * booking date/time picker both mark days with this, so the two calendars look the same.
 * Order doesn't matter. Both ends on one day: that day is both edges (the full range).
 * Only one end set: that day alone is marked (`solo`). Days compare by calendar day.
 */
export function rangeDayClasses(d: Date, a: Date | null | undefined, b: Date | null | undefined): RangeDayClasses {
  const lo = isDate(a) ? a : null;
  const hi = isDate(b) ? b : null;
  if (lo && hi) {
    const r = ordered(lo, hi);
    const t = dayOf(d).getTime();
    return {
      'edge-start': t === r.start.getTime(),
      'edge-end': t === r.end.getTime(),
      'in-range': t > r.start.getTime() && t < r.end.getTime(),
      solo: false,
    };
  }
  const only = lo ?? hi;
  const on = sameDay(d, only);
  return { 'edge-start': on, 'edge-end': false, 'in-range': false, solo: on };
}

/**
 * Preset labels arrive as "Weekly analytics (Sep 28, 2026 to Oct 4, 2026)".
 * Split them so the name and the dates can be shown on two lines.
 */
export function splitPresetLabel(label: string): { name: string; dates: string } {
  const m = /^(.*?)\s*\(([^()]*)\)\s*$/.exec(label ?? '');
  return m ? { name: m[1], dates: m[2] } : { name: label ?? '', dates: '' };
}

export interface CalendarCell {
  date: Date | null; // null = padding before the 1st
}

/**
 * The cells of one month grid, padded so the 1st lands under its weekday.
 * weekStartsOn: 0 = Sunday .. 6 = Saturday.
 */
export function monthCells(year: number, month: number, weekStartsOn: number): CalendarCell[] {
  const lead = (new Date(year, month, 1).getDay() - weekStartsOn + 7) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const cells: CalendarCell[] = [];
  for (let i = 0; i < lead; i++) cells.push({ date: null });
  for (let d = 1; d <= count; d++) cells.push({ date: new Date(year, month, d) });
  return cells;
}

/**
 * Card #219: format digits as they are typed into a date box, so 01012016
 * becomes 01/01/2016. A slash appears only once the next part starts, so
 * backspacing through "01/" never gets stuck. Text with letters ("Aug 3, 2026")
 * is left alone - month names can still be typed.
 */
export function maskDateDigits(text: string): string {
  if (/[a-zà-ÿ]/i.test(text ?? '')) return text;
  // Year first (2016-12-31, e.g. pasted from a spreadsheet): leave it for the parser.
  if (/^\s*\d{4}[\/.\-]/.test(text ?? '')) return text;
  // Keep the parts the person separated themselves ("3/8/2026" stays as typed);
  // only a part that runs past its size (day 2, month 2, year 4) spills into the next.
  const parts = (text ?? '')
    .replace(/^\s+/, '') // a leading space must not become a leading slash
    .replace(/[.,\-\s]/g, '/') // phone keypads offer "." or "," - both count as a slash
    .replace(/\/+/g, '/')
    .split('/')
    .map(p => p.replace(/\D/g, ''));
  const sizes = [2, 2, 4];
  const out: string[] = [];
  for (let i = 0; i < sizes.length && parts.length; i++) {
    let p = parts.shift()!;
    if (p.length > sizes[i]) {
      // The overflow joins the front of the next part, so a digit inserted
      // mid-date pushes the rest along instead of dropping it.
      const overflow = p.slice(sizes[i]);
      if (parts.length) parts[0] = overflow + parts[0];
      else parts.unshift(overflow);
      p = p.slice(0, sizes[i]);
    }
    out.push(p);
  }
  return out.join('/');
}

/** A typed date that is complete enough to apply: d/m/yyyy with 1-2 digit day and month. */
export function isCompleteTypedDate(text: string): boolean {
  return /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(text ?? '');
}

/** The year range the calendar will go to, shared by the year list and the jump. */
export const MIN_YEAR = 1900;
export const MAX_YEAR = 2200;

/** Years offered by the year selector: newest first, always including the ones in view. */
export function yearOptions(currentYear: number, ...mustInclude: number[]): number[] {
  const low = Math.max(MIN_YEAR, Math.min(2000, ...mustInclude));
  const high = Math.min(MAX_YEAR, Math.max(currentYear + 1, ...mustInclude));
  const years: number[] = [];
  for (let y = high; y >= low; y--) years.push(y);
  return years;
}

const MONTH_ABBR_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const two = (n: number) => ('0' + n).slice(-2);

/**
 * Card #213 step 3: the text the old Material range picker put in its boxes
 * ("03/Aug/2026", DD/MMM/YYYY, English months). Dashboard widgets save custom
 * filters to the server in exactly this form, so it must not change.
 */
export function legacyDateText(d: Date | null | undefined): string {
  return isRealDate(d) ? `${two(d.getDate())}/${MONTH_ABBR_EN[d.getMonth()]}/${d.getFullYear()}` : '';
}

/** A Date that holds an actual day (an "Invalid Date" object is truthy but holds none). */
function isRealDate(d: Date | null | undefined): d is Date {
  return d instanceof Date && !isNaN(d.getTime());
}

/** A date as YYYY-MM-DD (local calendar day), the form the report APIs take. */
export function isoDay(d: Date | null | undefined): string | null {
  return isRealDate(d) ? `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}` : null;
}

/** Preset options for the dashboard-widget style filters (labels translated by the caller). */
export function widgetRangeOptions(translate: (key: string) => string, includeNext: boolean): DateRangeFilterOption[] {
  const opts = [
    { value: 'current_month', key: 'HOME.Current_month' },
    { value: 'last_month', key: 'HOME.Last_month' },
    { value: 'last_3_month', key: 'HOME.Last_3_months' },
    { value: 'last_12_month', key: 'HOME.Last_12_months' },
  ];
  if (includeNext) {
    opts.push(
      { value: 'Next_3_months', key: 'HOME.Next_3_months' },
      { value: 'Next_6_months', key: 'HOME.Next_6_months' },
      { value: 'Next_12_months', key: 'HOME.Next_12_months' },
    );
  }
  return opts.map(o => ({ value: o.value, label: translate(o.key) }));
}

const WEEKDAYS_EN = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * A business-profile week start ("Monday", "sunday", ...) as 0..6 for the
 * calendar grid. Unknown or not loaded yet = Monday, the default the profile
 * load and the Weekly presets use.
 */
export function weekStartIndex(dayName: unknown): number {
  const i = WEEKDAYS_EN.indexOf(String(dayName ?? '').trim().toLowerCase());
  return i < 0 ? 1 : i;
}

/** Month arithmetic that never overflows (Jan 31 + 1 month is still February). */
export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

/** A calendar's month. */
export interface YearMonth {
  year: number;
  month: number;
}
/** The left and right calendars. */
export type CalendarPair = [YearMonth, YearMonth];

const monthIndex = (v: YearMonth) => v.year * 12 + v.month;
const ofDate = (d: Date): YearMonth => ({ year: d.getFullYear(), month: d.getMonth() });

/**
 * Card #213: the two calendars move independently, so a range can run from
 * Sep 2016 (left) to Oct 2026 (right). The left one always shows an earlier month;
 * when a move would cross them, the OTHER calendar steps aside to stay next to it.
 */
export function placeCalendar(pair: CalendarPair, index: 0 | 1, to: YearMonth): CalendarPair {
  const next: CalendarPair = index === 0 ? [to, pair[1]] : [pair[0], to];
  if (monthIndex(next[0]) < monthIndex(next[1])) return next;
  return index === 0
    ? [next[0], addMonths(next[0].year, next[0].month, 1)]
    : [addMonths(next[1].year, next[1].month, -1), next[1]];
}

/**
 * An arrow on calendar `index`. Side by side, both calendars page together (as
 * before); once they are apart, only the clicked one moves.
 */
export function stepCalendar(pair: CalendarPair, index: 0 | 1, delta: number): CalendarPair {
  if (monthIndex(pair[1]) - monthIndex(pair[0]) === 1) {
    return [addMonths(pair[0].year, pair[0].month, delta), addMonths(pair[1].year, pair[1].month, delta)];
  }
  const moved = addMonths(pair[index].year, pair[index].month, delta);
  return placeCalendar(pair, index, moved);
}

/**
 * The calendars for a range: the start's month on the left, the end's month on the
 * right. A range inside one month shows that month on the right (the calendar a
 * phone shows) with the month before it. With no range, last month and this month.
 */
export function calendarsFor(start: Date | null, end: Date | null, today: Date): CalendarPair {
  if (start && end) {
    const left = ofDate(start);
    return placeCalendar([left, addMonths(left.year, left.month, 1)], 1, ofDate(end));
  }
  if (start) {
    const left = ofDate(start);
    return [left, addMonths(left.year, left.month, 1)];
  }
  const right = ofDate(end ?? today);
  return [addMonths(right.year, right.month, -1), right];
}

/** Why a typed date can't be right (checked as it is typed); see typedDateProblem. */
export type TypedDateProblem =
  | { kind: 'day' }
  | { kind: 'month' }
  | { kind: 'year' }
  | { kind: 'dayInMonth'; year: number; month: number; days: number };

/**
 * Card #213: check a date while it is being typed, so "40/16/1900" is flagged at
 * "40" with what is wrong, not with "not recognised" once it is complete. A part is
 * judged only once it is finished (two digits for day and month, or followed by a
 * slash; four for the year). Year-first (2016-12-31) is checked the same way.
 * Text with letters (a month name) is left to parseTypedDate. null = nothing wrong yet.
 */
export function typedDateProblem(text: string): TypedDateProblem | null {
  const v = (text ?? '').trim();
  if (!v || /[a-zà-ÿ]/i.test(v)) return null;
  let day: string | undefined;
  let month: string | undefined;
  let year: string | undefined;
  let dayDone: boolean;
  let monthDone: boolean;
  const yearFirst = /^(\d{4})[\/.\-](\d{0,2})(?:[\/.\-](\d{0,2}))?$/.exec(v);
  if (yearFirst) {
    [, year, month, day] = yearFirst;
    monthDone = month.length === 2 || day !== undefined;
    dayDone = !!day && day.length === 2;
  } else {
    const parts = v.split(/[\/.\-]/);
    if (parts.length > 3 || parts.some(p => !/^\d*$/.test(p))) return null;
    [day, month, year] = parts;
    dayDone = day.length === 2 || parts.length > 1;
    monthDone = month !== undefined && (month.length === 2 || parts.length > 2);
  }
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (day && dayDone && (d < 1 || d > 31)) return { kind: 'day' };
  if (month && monthDone && (m < 1 || m > 12)) return { kind: 'month' };
  if (year && year.length === 4 && (y < MIN_YEAR || y > MAX_YEAR)) return { kind: 'year' };
  if (day && dayDone && month && monthDone && year && year.length === 4) {
    const days = new Date(y, m, 0).getDate();
    if (d > days) return { kind: 'dayInMonth', year: y, month: m - 1, days };
  }
  return null;
}

const MONTHS_EN = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** Lower case, no accents, no trailing dot: "Févr." -> "fevr". */
function plain(word: string): string {
  return word.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\.$/, '').trim();
}

/**
 * Month index for a typed word, or -1. A full or abbreviated name matches
 * exactly; otherwise a prefix (3+ letters) counts only when every month it
 * could start agrees - French "jui" could be juin or juillet, so it is
 * rejected rather than guessed.
 */
function monthIndexOf(word: string, names: string[][]): number {
  const w = plain(word);
  if (w.length < 3) return -1;
  for (const list of names) {
    const i = list.findIndex(n => plain(n) === w);
    if (i >= 0) return i;
  }
  const hits = new Set<number>();
  for (const list of names) list.forEach((n, i) => { if (plain(n).startsWith(w)) hits.add(i); });
  return hits.size === 1 ? [...hits][0] : -1;
}

/**
 * Parse what someone types into a date box. Accepts 03/08/2026 or 3-8-2026
 * (day first, as the app displays), 2026-08-03, and "Aug 3, 2026" / "3 Aug 2026"
 * (month names in English or in the lists passed in, e.g. the user's language).
 * Returns null for anything else, and for impossible dates like 31/02/2026.
 */
export function parseTypedDate(text: string, monthNames: string[][] = []): Date | null {
  const v = (text ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!v) return null;
  const build = (y: number, m: number, d: number): Date | null => {
    // Same range the year dropdown offers, so a typed year always has a matching option.
    if (y < MIN_YEAR || y > MAX_YEAR) return null;
    const x = new Date(y, m, d);
    return x.getFullYear() === y && x.getMonth() === m && x.getDate() === d ? x : null;
  };
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(v);
  if (m) return build(+m[1], +m[2] - 1, +m[3]);
  m = /^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/.exec(v);
  if (m) return build(+m[3], +m[2] - 1, +m[1]);
  const names = [MONTHS_EN, ...monthNames];
  const monthIndex = (word: string) => monthIndexOf(word, names);
  // The app's own display style: 03/Aug/2026.
  m = /^(\d{1,2})[\/\- ]([a-zà-ÿ.]+)[\/\- ](\d{4})$/.exec(v);
  if (m && monthIndex(m[2]) >= 0) return build(+m[3], monthIndex(m[2]), +m[1]);
  m = /^([a-zà-ÿ.]+) (\d{1,2}),? (\d{4})$/.exec(v);
  if (m && monthIndex(m[1]) >= 0) return build(+m[3], monthIndex(m[1]), +m[2]);
  m = /^(\d{1,2}) (?:de )?([a-zà-ÿ.]+),?(?: de)? (\d{4})$/.exec(v);
  if (m && monthIndex(m[2]) >= 0) return build(+m[3], monthIndex(m[2]), +m[1]);
  return null;
}
